import { api } from '@/lib/api';
import type {
  InboxCounts, InboxFilter, ListSendDetail, RejectedForward, ScheduledEntry, ThreadDetail, ThreadPage, TriageDetail, TriageListItem,
} from '@shared/mail';

// Query keys and fetchers for the Inbox. Everything sits under ['inbox'] or ['triage'], which the server invalidates
// whenever mail, reads, triage or the outbox change.

export type ConversationFilter = Exclude<InboxFilter, 'triage' | 'scheduled'>;

export interface TriageList {
  items: TriageListItem[];
  rejected: (RejectedForward & { teammate: { id: string; name: string } | null })[];
}

export interface LatestReply {
  threadId: string;
  name: string;
  subject: string;
}

export interface LatestList {
  name: string | null;
  personIds: string[];
}

export const countsQuery = {
  queryKey: ['inbox', 'counts'],
  queryFn: () => api.get<InboxCounts>('/inbox/counts'),
  staleTime: 30_000,
} as const;

export function threadsKey(filter: ConversationFilter, q: string): unknown[] {
  return ['inbox', 'threads', filter, q];
}

export function fetchThreads(filter: ConversationFilter, q: string, cursor: string | null): Promise<ThreadPage> {
  return api.get<ThreadPage>('/inbox/threads', { query: { filter, q, cursor } });
}

export function threadQuery(id: string) {
  return { queryKey: ['inbox', 'thread', id], queryFn: () => api.get<ThreadDetail>(`/inbox/threads/${id}`) } as const;
}

export const triageQuery = { queryKey: ['triage', 'list'], queryFn: () => api.get<TriageList>('/inbox/triage') } as const;

export function triageItemQuery(id: string) {
  return { queryKey: ['triage', 'item', id], queryFn: () => api.get<TriageDetail>(`/inbox/triage/${id}`) } as const;
}

export const scheduledQuery = { queryKey: ['inbox', 'scheduled'], queryFn: () => api.get<ScheduledEntry[]>('/inbox/scheduled') } as const;

export function listSendQuery(id: string) {
  return { queryKey: ['inbox', 'list-send', id], queryFn: () => api.get<ListSendDetail>(`/mail/list-sends/${id}`) } as const;
}

export const latestReplyQuery = {
  queryKey: ['inbox', 'latest-reply'],
  queryFn: () => api.get<LatestReply | null>('/inbox/latest-reply'),
  staleTime: 60_000,
} as const;

export const latestListQuery = {
  queryKey: ['inbox', 'latest-list'],
  queryFn: () => api.get<LatestList | null>('/inbox/latest-list'),
  staleTime: 60_000,
} as const;
